import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Gem, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import nailsImage from "@/assets/gelnaegel-nagelstudio-nuernberg.webp";
import nailsVideo from "@/assets/nageldesign-video-nuernberg.webm";
import SEO from "@/components/SEO";

const PLANITY_URL = "https://www.planity.com/de-DE/22-studios-90469-nurnberg";

const NaegelPage = () => {
  return (
    <main className="min-h-screen bg-background relative">
      <SEO
        title="Nagelstudio Nürnberg – Gelnägel & Maniküre | 22 Studios"
        description="Nagelstudio in Nürnberg für Gelnägel, russische Maniküre, Shellac & medizinische Pediküre. Kreatives Nageldesign ab 55 € – jetzt online Termin buchen!"
        canonical="https://22studios.de/nuernberg/naegel"
      />
      <AnimatedBackground />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-24 pb-10 md:pt-40 md:pb-24 relative z-10">
        <div className="container px-4 md:px-6">

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
            <div className="space-y-4 md:space-y-6">
              <div className="space-y-3 md:space-y-4">
                <span className="text-sm tracking-wide uppercase text-label font-medium">
                  Maniküre & Pediküre in Nürnberg
                </span>
                <h1 className="text-display md:text-display-lg font-serif font-light text-foreground">
                  Nagelstudio in Nürnberg
                </h1>
              </div>
              <p className="text-base md:text-lg text-muted-foreground font-light leading-relaxed">
                Dein Nagelstudio in Nürnberg für professionelle Maniküre, Pediküre, Gelnägel und Shellac. Kreative Nageldesigns, die deine Persönlichkeit widerspiegeln – modern, gepflegt und stilvoll.
              </p>
              <div className="flex flex-wrap gap-2">
                {["Maniküre", "Pediküre", "Gelnägel", "Shellac"].map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium rounded-full bg-secondary/50 text-muted-foreground border border-border/30"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-blush-dark px-8 py-6 text-base font-medium rounded-full hover-lift shadow-glow"
                onClick={() => window.open(PLANITY_URL, '_blank')}
              >
                Jetzt Termin buchen
              </Button>
            </div>
            <div className="image-overlay rounded-2xl overflow-hidden shadow-soft-lg hover-pop">
              <video
                src={nailsVideo}
                poster={nailsImage}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-full h-64 md:h-80 lg:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 md:py-28 bg-secondary/30 relative z-10">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <div className="flex items-center justify-center gap-4 mb-4 md:mb-6">
              <span className="w-12 h-px bg-primary" />
              <span className="text-sm tracking-wide uppercase text-label font-medium">
                Preise
              </span>
              <span className="w-12 h-px bg-primary" />
            </div>
            <h2 className="text-heading md:text-display font-serif font-light text-foreground">
              Preise – Maniküre, Gelnägel & Pediküre
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-4xl mx-auto">
            {/* Maniküre */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Gem className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Maniküre</h3>
              </div>
              <ul className="space-y-2 md:space-y-4">
                <li className="py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Russische Maniküre</span>
                    <span className="font-medium">55 €</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">Shellack, Farbe</p>
                </li>
              </ul>
            </div>

            {/* Gelnägel */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Gelnägel</h3>
              </div>
              <ul className="space-y-2 md:space-y-4">
                <li className="py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Auffüllen ohne Farbe</span>
                    <span className="font-medium">59 €</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">inkl. Maniküre</p>
                </li>
                <li className="py-2 md:py-3 border-b border-border/50 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Auffüllen mit Farbe</span>
                    <span className="font-medium">65 €</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">inkl. Design, French</p>
                </li>
                <li className="py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Neues Set</span>
                    <span className="font-medium">99 €</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">inkl. Farbe</p>
                </li>
              </ul>
            </div>

            {/* Extras & Extreme */}
            <div className="bg-background rounded-2xl p-5 md:p-8 shadow-soft hover-pop group md:col-span-2">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-foreground" />
                </div>
                <h3 className="text-lg md:text-xl font-serif font-medium">Extras & Spezial</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                <div className="py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Aufpreis lange Nägel</span>
                    <span className="font-medium">5 €</span>
                  </div>
                </div>
                <div className="py-2 md:py-3 hover:bg-primary/5 px-2 rounded transition-colors">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium text-sm">Extreme Formen - Neues Set</span>
                    <span className="font-medium">119 €</span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">inkl. Farbe, Design</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-10 md:mt-16">
            <Button
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-blush-dark px-10 py-6 text-base font-medium rounded-full hover-lift shadow-glow"
              onClick={() => window.open(PLANITY_URL, '_blank')}
            >
              Jetzt Termin buchen
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default NaegelPage;